import React, { useMemo, useState } from 'react'

import { useGroupActions } from 'redux/actions/Groups'

import { useAccounts } from 'redux/hooks/Accounts'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useGroup } from 'redux/hooks/Groups'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Checkbox from 'components_new/atoms/Checkbox'
import Skeleton from 'components_new/atoms/Skeleton'

import GenericListItem from 'components_new/molecules/GenericListItem'
import SelectSearch, { Option } from 'components_new/molecules/SelectSearch'
import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'
import SimpleDialog from 'components_new/molecules/SimpleDialog'

import { formatUserOption } from 'utils/functions'

interface AddToGroupDialogProps {
  id: string
  onClose: () => void
  open: boolean
}

const AddToGroupDialog = (props: AddToGroupDialogProps) => {
  const { id, onClose, open } = props

  const { t } = useTranslation()
  const { tryInviteToGroup } = useGroupActions()
  const { accounts, loading: accountsLoading } = useAccounts()
  const { loading: accountLoading } = useAccountSettings()
  const group = useGroup(id)

  const [pendingUsers, setPendingUsers] = useState<Option[]>([])
  const [notifyUsers, setNotifyUsers] = useState(true)

  const options = useMemo(() => {
    if (!group) {
      return []
    }

    const currentMembers: { [accountId: string]: true } = {}

    group.active_members.forEach((member) => {
      currentMembers[member.account_id] = true
    })
    group.pending.forEach((member) => {
      currentMembers[member.account_id] = true
    })

    return accounts
      .filter((account) => !currentMembers[account.id])
      .map(formatUserOption)
  }, [accounts, group])

  const loading = accountLoading || !group || accountsLoading

  const handleClose = () => {
    setPendingUsers([])
    onClose()
  }

  const handleSubmit = () => {
    if (!group) return

    tryInviteToGroup(
      group.id,
      pendingUsers.map((user) => user.id as string),
      false,
      notifyUsers
    )

    handleClose()
  }

  return (
    <SimpleDialog
      actions={
        <>
          <Button variant="text" onClick={handleClose}>
            {t('global', 'cancel')}
          </Button>
          <Button
            disabled={pendingUsers.length === 0 || loading}
            variant="contained"
            onClick={handleSubmit}
          >
            {t('global', 'invite')}
          </Button>
        </>
      }
      contentText={
        <>
          {t('groups', 'expand_group') + ' '}
          <Box sx={{ display: 'inline-block' }}>
            {loading ? (
              <Skeleton variant="text" width={96} />
            ) : (
              <GenericListItem
                color={group.profile_color}
                component="div"
                disablePadding={true}
                size="small"
                title={group.name}
                variant="group"
              />
            )}
          </Box>
          {' ' + t('groups', 'by_inviting_more_members', true)}
        </>
      }
      onClose={handleClose}
      open={open}
      title={t('groups', 'invite_members')}
    >
      {loading ? (
        <Skeleton height={64} variant="rounded" width="100%" />
      ) : (
        <>
          <SelectSearch
            color="primary"
            onChange={setPendingUsers}
            multiple={true}
            options={options}
            selected={pendingUsers}
            size={'medium'}
            sort={true}
          />
          <SettingsGroup>
            <SettingsItem
              body={t('groups', 'notify_users_body')}
              icon={
                <Checkbox
                  checked={notifyUsers}
                  onChange={() => setNotifyUsers(!notifyUsers)}
                  size="small"
                />
              }
              title={t('groups', 'notify_users_title')}
            />
          </SettingsGroup>
        </>
      )}
    </SimpleDialog>
  )
}

export default AddToGroupDialog
